import type { ReactNode } from 'react';

/**
 * Shared SCHEDULED|ACTUAL grid cell markup for the Costing / Pricing tabs —
 * exact transcription of the repeated costing.tmpl / pricing.tmpl nestings
 * (§B2/§B3; the verify harness compares this DOM).
 */

/** `<div class="unit col-xs-N"><div class=""><span>{unit}</span></div></div>` */
export const UnitBox = ({ cols, unit }: { cols: number; unit: string }) => (
  <div className={`unit col-xs-${cols}`}>
    <div className="">
      <span>{unit}</span>
    </div>
  </div>
);

/** Left DESCRIPTION cell. */
export const GridLabel = ({ text }: { text: string }) => (
  <div className="col-xs-3">
    <div className="row label-content">
      <span className="label-text">{text}</span>
    </div>
  </div>
);

/** `.col-xs-12 > .row.{className} > [label | .col-xs-9 > .row > children]` */
export const GridRow = ({
  label,
  className = 'costing-item',
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) => (
  <div className="col-xs-12">
    <div className={`row ${className}`}>
      <GridLabel text={label} />
      <div className="col-xs-9">
        <div className="row">{children}</div>
      </div>
    </div>
  </div>
);

/** Single-value column, unit + input right-aligned (`.col-xs-8.col-xs-offset-4`). */
export const SingleUnitCol = ({ unit, children }: { unit: string; children: ReactNode }) => (
  <div className="col-xs-6">
    <div className="row ">
      <div className="col-xs-8 col-xs-offset-4">
        <div className="row">
          <UnitBox cols={6} unit={unit} />
          <div className="col-xs-6">
            <div className="row">{children}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

/** Money column (`.col-xs-6.col-xs-offset-6` wrap, unit col-xs-4 / input col-xs-8). */
export const MoneyCol = ({ children }: { children: ReactNode }) => (
  <div className="col-xs-6">
    <div className="row ">
      <div className="col-xs-6 col-xs-offset-6">
        <div className="row">
          <UnitBox cols={4} unit="US$" />
          <div className="col-xs-8">
            <div className="row">{children}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

/** Unit-less column (List Price Factor / Multiplier cells). */
export const PlainCol = ({ children }: { children: ReactNode }) => (
  <div className="col-xs-6">
    <div className="row ">
      <div className="col-xs-6 col-xs-offset-6">
        <div className="row">
          <div className="col-xs-8 col-xs-offset-4">
            <div className="row">{children}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

/** %/US$ pair column (percent: unit 8/input 4; amount: unit 4/input 8). */
export const PctAmtCol = ({
  pct,
  amt,
  offset,
}: {
  pct: ReactNode;
  amt: ReactNode;
  offset?: boolean;
}) => (
  <div className={`col-xs-6${offset ? ' col-xs-offset-6' : ''}`}>
    <div className="row ">
      <div className="col-xs-6">
        <div className="row">
          <UnitBox cols={8} unit="%" />
          <div className="col-xs-4">
            <div className="row">{pct}</div>
          </div>
        </div>
      </div>
      <div className="col-xs-6">
        <div className="row">
          <UnitBox cols={4} unit="US$" />
          <div className="col-xs-8">
            <div className="row">{amt}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
);
